import { PillColor } from '@/components/appointment-pill.tsx';

import { DayjsUnit } from '@/models/Dayjs.ts';
import { GenericPatientInsurance } from '@/models/GenericPatient.ts';
import { IndicatorStatus } from '@/models/IndicatorStatus.ts';
import { Appointment, Insurance } from '@/models/appointment/Appointment.ts';
import { AppointmentTypeInsurances } from '@/models/appointment/AppointmentTypeInsurances.ts';
import { EnrichedAppointmentType } from '@/models/appointment/EnrichedPractitionerData.ts';
import { InsuranceType } from '@/models/insurance.ts';

import { dayjs } from '@/utils/dayjsSetup.ts';

const CARD_SWEEP_THRESHOLD_MONTHS = 3;

export const INSURANCE_OPTIONS = [
    { value: InsuranceType.PUBLIC, label: 'GKV (Public)' },
    { value: InsuranceType.PRIVATE, label: 'PKV (Private)' },
    { value: InsuranceType.SELF_PAYER, label: 'Selbstzahler (Self-paying)' },
];

export const INSURANCE_TYPE_TO_INSURANCE: Record<InsuranceType, Insurance> = {
    [InsuranceType.PUBLIC]: Insurance.PUBLIC,
    [InsuranceType.PRIVATE]: Insurance.PRIVATE,
    [InsuranceType.SELF_PAYER]: Insurance.SELF_PAYER,
};

const matchesInsuranceType = (apt: EnrichedAppointmentType, insurance: InsuranceType): boolean => {
    switch (insurance) {
        case InsuranceType.PUBLIC: {
            return apt.is_for_public_insurance;
        }
        case InsuranceType.PRIVATE: {
            return apt.is_for_private_insurance;
        }
        case InsuranceType.SELF_PAYER: {
            return apt.is_for_self_payers;
        }
        default: {
            return false;
        }
    }
};

export const filterAppointmentTypesByInsurance = (
    appointmentTypes: EnrichedAppointmentType[],
    insuranceTypes: InsuranceType[]
): EnrichedAppointmentType[] => {
    if (insuranceTypes.length === 0) return appointmentTypes;

    return appointmentTypes.filter((apt) => insuranceTypes.every((t) => matchesInsuranceType(apt, t)));
};

export const getInsuranceTypeDisplay = (insurance: string): Insurance => {
    return Insurance[insurance.toUpperCase() as keyof typeof Insurance] || '';
};

export const insuranceToInsuranceType = (insurance: Insurance): InsuranceType => {
    switch (insurance) {
        case Insurance.PRIVATE: {
            return InsuranceType.PRIVATE;
        }
        case Insurance.PUBLIC: {
            return InsuranceType.PUBLIC;
        }
        default: {
            return InsuranceType.SELF_PAYER;
        }
    }
};

export const getInsuranceStatus = (insurance: GenericPatientInsurance): IndicatorStatus => {
    const insurance_type: Insurance = getInsuranceTypeDisplay(insurance.type);
    if (insurance_type === Insurance.PRIVATE || insurance_type === Insurance.SELF_PAYER) {
        return IndicatorStatus.UNKNOWN;
    }

    const swipeDate = insurance.card_swipe_date ? dayjs(insurance.card_swipe_date) : undefined;
    const isActive =
        swipeDate !== undefined &&
        !swipeDate.isAfter(dayjs()) &&
        dayjs().diff(swipeDate, DayjsUnit.MONTH) <= CARD_SWEEP_THRESHOLD_MONTHS;

    return isActive ? IndicatorStatus.ACTIVE : IndicatorStatus.INACTIVE;
};

export const getInsuranceTypeFromAppointment = (appointment: Appointment): string =>
    appointment.data.insurance_coverage_type?.toLowerCase() === 'selfpayer'
        ? 'SELF_PAYER'
        : (appointment.data?.insurance_coverage_type?.toUpperCase() ?? '');

const INSURANCE_TYPES: readonly string[] = Object.values(InsuranceType);

/** The appointment's Versicherungsart, or `undefined` for the `'N/A'` or empty value the form-data API rejects. */
export const getValidInsuranceTypeFromAppointment = (appointment: Appointment): InsuranceType | undefined => {
    const insuranceType = getInsuranceTypeFromAppointment(appointment);

    return INSURANCE_TYPES.includes(insuranceType) ? (insuranceType as InsuranceType) : undefined;
};

export const getInsuranceColor = (isActive: boolean, insurance?: Insurance): PillColor => {
    if (isActive) return 'green';
    if (insurance === Insurance.PUBLIC) return 'red';
    return 'blue';
};

const INSURANCE_CHECKIN_OPTIONS: { value: InsuranceType; label: string }[] = [
    { value: InsuranceType.PUBLIC, label: 'GKV' },
    { value: InsuranceType.PRIVATE, label: 'PKV' },
    { value: InsuranceType.SELF_PAYER, label: 'Selbstzahler' },
];

/**
 * Returns the insurance options that are allowed for the given appointment type,
 * based on its is_for_public_insurance / is_for_private_insurance / is_for_self_payers flags.
 * Falls back to all three options when the appointment type is unknown or has no flags set.
 */
export const getAllowedInsuranceOptions = (
    appointmentType?: EnrichedAppointmentType
): { value: InsuranceType; label: string }[] => {
    if (!appointmentType) return INSURANCE_CHECKIN_OPTIONS;

    const options = INSURANCE_CHECKIN_OPTIONS.filter(({ value }) => {
        if (value === InsuranceType.PUBLIC) return appointmentType.is_for_public_insurance;
        if (value === InsuranceType.PRIVATE) return appointmentType.is_for_private_insurance;
        if (value === InsuranceType.SELF_PAYER) return appointmentType.is_for_self_payers;
        return false;
    });

    return options.length > 0 ? options : INSURANCE_CHECKIN_OPTIONS;
};

export const checkInsuranceIsOffered = (
    apt_type_id: string,
    insurance: Insurance,
    apt_type_insurance_map: AppointmentTypeInsurances
): boolean => {
    const insurance_list = apt_type_insurance_map.get(apt_type_id);
    if (!insurance_list) return true;

    return insurance_list.includes(insurance);
};
